import React, { useState, useMemo } from 'react';
import { Form } from 'react-bootstrap';
import '../css/advancedProducts.css';

// Column categories for grouping
const COLUMN_GROUPS = {
    'General': ['primaryImage', 'sku', 'title', 'currentDescription', 'bullets', 'brand'],
    'Shopify': ['price', 'compareAtPrice', 'size', 'barcode', 'status', 'seoTitle', 'descriptionHtml'],
    'Inventory': ['ecomm_onhand', 'productFactor', 'weightOz'],
    'Costing': ['cost', 'estShippingCost'],
    'Sales Channels': ['channels', 'amazon.asin'],
    'Organization': ['tags', 'vendor', 'productCategory', 'productType'],
};

export const ColumnTogglePopover = ({ gridRef, onClose }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [columnStates, setColumnStates] = useState(() => {
        // Initialize from current column visibility
        const states = {};
        if (gridRef?.current?.columnApi) {
            const allColumns = gridRef.current.columnApi.getColumns();
            allColumns?.forEach(col => {
                states[col.getColId()] = col.isVisible();
            });
        }
        return states;
    });

    // Get all columns from grid
    const allColumns = useMemo(() => {
        if (!gridRef?.current?.columnApi) return [];
        return gridRef.current.columnApi.getColumns()?.map(col => ({
            colId: col.getColId(),
            headerName: col.getColDef().headerName || col.getColDef().field || col.getColId(),
            field: col.getColDef().field,
            visible: col.isVisible()
        })) || [];
    }, [gridRef]);

    // Filter columns based on search
    const filteredColumns = useMemo(() => {
        if (!searchTerm) return allColumns;
        const term = searchTerm.toLowerCase();
        return allColumns.filter(col => 
            col.headerName?.toLowerCase().includes(term) ||
            col.field?.toLowerCase().includes(term)
        );
    }, [allColumns, searchTerm]);

    // Group columns by category
    const groupedColumns = useMemo(() => {
        const grouped = {};
        const assignedFields = new Set();

        // Assign columns to groups
        Object.entries(COLUMN_GROUPS).forEach(([groupName, fields]) => {
            const groupColumns = filteredColumns.filter(col => 
                fields.includes(col.field) || fields.includes(col.colId)
            );
            if (groupColumns.length > 0) {
                grouped[groupName] = groupColumns;
                groupColumns.forEach(col => assignedFields.add(col.colId));
            }
        });

        // Add ungrouped columns to "Other"
        const otherColumns = filteredColumns.filter(col => 
            !assignedFields.has(col.colId) && 
            col.colId !== 'parentSku' && // Skip internal grouping column
            col.colId !== '0' // Skip checkbox column
        );
        if (otherColumns.length > 0) {
            grouped['Other'] = otherColumns;
        }

        return grouped;
    }, [filteredColumns]);

    const handleToggleColumn = (colId) => {
        const newVisible = !columnStates[colId];
        setColumnStates(prev => ({ ...prev, [colId]: newVisible }));
        
        if (gridRef?.current?.columnApi) {
            gridRef.current.columnApi.setColumnVisible(colId, newVisible);
        }
    };

    const handleSelectAll = () => {
        const newStates = {};
        allColumns.forEach(col => {
            newStates[col.colId] = true;
            if (gridRef?.current?.columnApi) {
                gridRef.current.columnApi.setColumnVisible(col.colId, true);
            }
        });
        setColumnStates(newStates);
    };

    const handleDeselectAll = () => {
        const newStates = {};
        allColumns.forEach(col => {
            // Keep a few essential columns visible
            const keepVisible = ['primaryImage', 'sku', 'title'].includes(col.field);
            newStates[col.colId] = keepVisible;
            if (gridRef?.current?.columnApi) {
                gridRef.current.columnApi.setColumnVisible(col.colId, keepVisible);
            }
        });
        setColumnStates(newStates);
    };

    return (
        <div className="column-toggle-popover">
            <div className="popover-header">
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <span>Show/Hide Columns</span>
                    <div style={{ display: 'flex', gap: '8px' }}>
                        <button 
                            onClick={handleSelectAll}
                            style={{ 
                                fontSize: '11px', 
                                background: 'none', 
                                border: 'none', 
                                color: '#2c7be5', 
                                cursor: 'pointer',
                                padding: 0
                            }}
                        >
                            Select All
                        </button>
                        <button 
                            onClick={handleDeselectAll}
                            style={{ 
                                fontSize: '11px', 
                                background: 'none', 
                                border: 'none', 
                                color: '#2c7be5', 
                                cursor: 'pointer',
                                padding: 0
                            }}
                        >
                            Clear
                        </button>
                    </div>
                </div>
                <input
                    type="text"
                    className="search-input"
                    placeholder="Search fields..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoFocus
                />
            </div>
            
            <div style={{ padding: '8px 0' }}>
                {Object.entries(groupedColumns).map(([groupName, columns]) => (
                    <div key={groupName} className="column-group">
                        <div className="column-group-title">{groupName}</div>
                        {columns.map(col => (
                            <div 
                                key={col.colId} 
                                className="column-item"
                                onClick={() => handleToggleColumn(col.colId)}
                            >
                                <Form.Check
                                    type="checkbox"
                                    checked={columnStates[col.colId] ?? col.visible}
                                    onChange={() => handleToggleColumn(col.colId)}
                                    onClick={(e) => e.stopPropagation()}
                                />
                                <label>{col.headerName}</label>
                            </div>
                        ))}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default ColumnTogglePopover;
